import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CheckCircle, XCircle, Clock, DollarSign, ChevronLeft, ChevronRight } from "lucide-react";

const PaymentTransactionsViewer = ({ API, token }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchTransactions();
  }, [page]);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/payment-transactions?skip=${page * limit}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const transactionsData = Array.isArray(response.data.transactions) ? response.data.transactions : [];
      setTransactions(transactionsData);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to fetch payment transactions");
      setTransactions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: { variant: "success", icon: CheckCircle, label: "Success" },
      pending: { variant: "warning", icon: Clock, label: "Pending" },
      failed: { variant: "destructive", icon: XCircle, label: "Failed" }
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading transactions...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">Payment Transactions</h3>
          <p className="text-gray-600">View all payment transactions and their status</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600">Total Transactions</p>
          <p className="text-2xl font-bold">{total}</p>
        </div>
      </div>

      {transactions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h4 className="text-lg font-semibold mb-2">No Transactions Yet</h4>
            <p className="text-gray-600">Payment transactions will appear here once users make payments</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Gateway</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell className="font-mono text-xs">
                          {transaction.id.substring(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{transaction.user_email}</p>
                            <p className="text-xs text-gray-600">
                              ID: {transaction.user_id.substring(0, 8)}...
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{transaction.plan_name || "N/A"}</Badge>
                        </TableCell>
                        <TableCell className="font-semibold">
                          {transaction.currency === "INR" ? "₹" : transaction.currency === "USD" ? "$" : "€"}
                          {transaction.amount}
                        </TableCell>
                        <TableCell className="capitalize">
                          <div className="flex items-center gap-2">
                            {transaction.gateway}
                            {transaction.metadata?.mock_mode && (
                              <Badge variant="secondary" className="text-xs">Mock</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {new Date(transaction.created_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} transactions
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * limit >= total}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentTransactionsViewer;
